import * as Apollo from "@apollo/client";
import { pipe, TaskEither } from "fp";

import * as Data from "~/Data";
import * as ErrorMessage from "~/ErrorMessage";
import { Remote as GenericRemote } from "~/Remote";
import { Request } from "~/Request";
import { WebAPI } from "~/WebAPI";

/**
 * Cancels a pending revision suggestion. Uses `suggestion.id` as-is — it's either
 * the internal UUID (migrated drivers) or the stringified legacy ES API id
 * (non-migrated / pre-existing suggestions), and the backend mutation accepts
 * either form (SC-508345 — see this MR's description).
 */
export const request = (client: Apollo.ApolloClient<unknown>) => (
  suggestion: Data.Reference.Reference
): Request.Fn<void> => () =>
  pipe(
    TaskEither.fromUnsafe(() =>
      client.mutate<
        WebAPI.Revision.UpdateRevisionSuggestionResult,
        WebAPI.Revision.UpdateRevisionSuggestionVariables
      >({
        mutation: WebAPI.Revision.UpdateRevisionSuggestionDocument,
        variables: { suggestionId: suggestion.id, status: "cancelled" },
      })
    ),
    TaskEither.mapLeft((error) => {
      console.error(error);
      return Error(
        `Something is wrong with your connection, ${ErrorMessage.Remedy.Retry.later()} or ${ErrorMessage.Remedy.contactSupport()}.`
      );
    }),
    TaskEither.chain((result) =>
      result.data?.updateRevisionSuggestion.success
        ? TaskEither.right(undefined)
        : TaskEither.left(
            Error(
              `Something is wrong with our data, ${ErrorMessage.Remedy.contactSupport()}.`
            )
          )
    )
  );

export namespace Delete {
  export const use = () => GenericRemote.Mutation.use(request(WebAPI.useClient()));
}
